import NumField from '@/components/fields/NumField';
import { modal } from '@/components/modal/Modal';
import EditDataTable from '@/components/table/EditDataTable';
import { useKeycloak } from '@/contexts/KeycloakContext';
import { useSliderRange } from '@/hooks/useSliderRange';
import {
	ScenarioContentContainer,
	ScenarioMainContent,
} from '@/styles/scenario';
import axios from '@/utils/axios';
import {
	CURRENT_YEAR,
	YEARS_TO_ADD,
	YEARS_TO_SUBTRACT,
	convertScenariosToList,
	currentYear,
	exists,
	getScenarioCurrentRange,
	isInRange,
} from '@/utils/utils';
import Tippy from '@tippyjs/react';
import { produce } from 'immer';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import CommonScenarioHeader from '../CommonScenarioHeader';
import DemandChart from './DemandChart';
import DemandConfig from './DemandConfig';
import DemandTree from './DemandTree';

const Demand: React.FC = () => {
	const { keycloak } = useKeycloak();
	const [scenarioId, setScenarioId] = useState<TValue>();
	const [scenarioData, setScenarioData] = useState<TDemandScenarioData[]>([]);
	const initialScenarioDataRef = useRef<TDemandScenarioData[] | null>(null);

	const {
		value: yearRange,
		setValue: setYearRange,
		min,
		max,
	} = useSliderRange({
		currentValues: [CURRENT_YEAR, CURRENT_YEAR + YEARS_TO_ADD],
		min: Math.max(2019, CURRENT_YEAR - YEARS_TO_SUBTRACT),
		max: CURRENT_YEAR + YEARS_TO_ADD,
	});

	const { data: scenarios } = useQuery(
		['demand_scenarios'],
		() => axios.get<TScenario[]>('/demand_scenarios'),
		{
			select: ({ data }) => convertScenariosToList(data),
		}
	);

	const [selectedItem, setSelectedItem] = useState<TDemandTreeSelectedItem>();

	const isSalesRegionSelected = selectedItem?.depth === 1;

	const { data: scenario, refetch: refetchScenario } = useQuery(
		['demand_scenario', scenarioId, selectedItem?.value, yearRange],
		() =>
			axios.get<TDemandScenarioData[]>(`/demand_scenario`, {
				params: {
					scenario_id: scenarioId,
					sales_region: selectedItem!.item.sales_region,
					...(selectedItem!.depth === 2
						? {
								[selectedItem!.item.fam_type === 'epv'
									? 'epv_family'
									: 'horizon_family']: selectedItem!.item.family_code,
						  }
						: undefined),
					year_start: yearRange[0],
					year_end: yearRange[1],
				},
			}),
		{
			enabled: !!scenarioId && !!selectedItem?.item,
			cacheTime: 0,
			onSuccess: ({ data }) => {
				initialScenarioDataRef.current = data;
			},
		}
	);

	const hasScenarioBeenUpdated =
		JSON.stringify(initialScenarioDataRef.current) ===
		JSON.stringify(scenarioData);

	const { mutate: updateDemand } = useMutation(
		['update_demand', yearRange],
		({
			year,
			pc_growth,
			read_scenario,
		}: {
			year: number;
			pc_growth: number;
			read_scenario: boolean;
		}) =>
			axios.post<TDemandScenarioData[]>(
				`/demand_updates?read_scenario=${read_scenario}&year_start=${yearRange[0]}&year_end=${yearRange[1]}`,
				{
					demand_scenario_id: scenarioId,
					year,
					pc_growth,
					sales_region: selectedItem!.item.sales_region,
					...(isSalesRegionSelected
						? null
						: {
								[selectedItem!.item.fam_type === 'epv'
									? 'epv_family_id'
									: 'horizon_family_id']: selectedItem!.item.family_code,
						  }),
					last_modified_by: keycloak?.tokenParsed?.preferred_username,
					last_modified_datetime: new Date(),
				}
			),
		{
			onSuccess: (data, { year, pc_growth }) => {
				if (data.data) {
					setScenarioData(data.data);
				} else {
					setScenarioData((prev) =>
						produce(prev, (draft) => {
							const index = draft.findIndex((item) => item.year === year);
							draft[index].pc_growth = pc_growth;
						})
					);
				}
			},
		}
	);

	useEffect(() => {
		if (scenario) {
			setScenarioData(scenario.data);
		}
	}, [scenario]);

	const formattedScenario = useMemo(() => {
		if (!scenario) return [];

		const fieldNames: (keyof TDemandScenarioData)[] = isSalesRegionSelected
			? ['pc_growth_regional', 'pc_growth', 'total']
			: ['pc_growth', 'total'];
		return fieldNames.map((field) =>
			scenarioData.reduce(
				(acc, curr) => ({ ...acc, [curr.year]: curr[field] }),
				{} as Record<string | number, TValue>
			)
		)!;
	}, [scenarioData, isSalesRegionSelected]);

	const formattedScenarioWithYear = useMemo(() => {
		return structuredClone(formattedScenario).map((row) => {
			Object.keys(row).map((year) => {
				if (!isInRange(parseInt(year), yearRange)) {
					delete row[year];
				}
			});
			return row;
		});
	}, [formattedScenario, yearRange]);

	useEffect(() => {
		setSelectedItem(undefined);
	}, [scenarioId]);

	return (
		<div>
			<CommonScenarioHeader
				scenarioId={scenarioId}
				setScenarioId={setScenarioId}
				scenarios={scenarios}
				type='demand'
			/>
			<ScenarioContentContainer>
				<ScenarioMainContent>
					<DemandTree
						scenarioId={scenarioId}
						selectedItem={selectedItem}
						setSelectedItem={setSelectedItem}
					/>
					<DemandChart
						scenario={formattedScenarioWithYear}
						isSalesRegionSelected={isSalesRegionSelected}
						title={(selectedItem?.value as string) ?? ''}
					/>
				</ScenarioMainContent>
				<DemandConfig
					setScenarioData={setScenarioData}
					scenarioId={scenarioId}
					scenarios={scenarios}
					selectedItem={selectedItem}
					setSelectedItem={setSelectedItem}
					min={min}
					max={max}
					yearRange={yearRange}
					setYearRange={setYearRange}
					currentScenarioRange={getScenarioCurrentRange(
						formattedScenarioWithYear
					)}
					refetchScenario={refetchScenario}
					hasScenarioBeenUpdated={hasScenarioBeenUpdated}
				/>

				{selectedItem?.item && formattedScenarioWithYear.length > 0 && (
					<EditDataTable
						rowsData={formattedScenarioWithYear}
						headers={Object.keys(formattedScenarioWithYear[0])}
						firstCol={
							isSalesRegionSelected
								? ['%Yoy Default', '%Yoy Average', 'Tonnes as is']
								: ['%Yoy Default', 'Tonnes as is']
						}
						renderRow={({
							row,
							fieldName,
							rowIndex,
							colIndex,
							onFocus,
							onDragOver,
							rowHeader,
						}) => {
							const _disabled = (fieldName as number) <= currentYear();
							const scenario = scenarioData[colIndex];
							const origin =
								rowIndex === 0 && isSalesRegionSelected
									? 'origin_regional'
									: 'origin';
							const isFamilyUpdate = scenario.origin === 'Family update';
							const shouldRound = rowHeader!.includes('%Yoy');
							return (
								<Tippy
									content={
										scenario !== undefined && (
											<>
												<p>{scenario[origin]}</p>
												{isSalesRegionSelected &&
													isFamilyUpdate &&
													rowIndex !== 0 && <p>{scenario.families_edited}</p>}
											</>
										)
									}
									placement='bottom'
									key={colIndex}>
									<td
										className={_disabled ? 'disabled' : undefined}
										onDragOver={!_disabled ? onDragOver : undefined}>
										<NumField
											value={row[fieldName] as number}
											setValue={(v) => {
												updateDemand({
													year: +fieldName,
													pc_growth: v as number,
													read_scenario: isSalesRegionSelected,
												});
											}}
											scale={shouldRound ? 1 : 0}
											round={!shouldRound}
											disabled={_disabled || rowIndex !== 0}
											onPaste={(e) => {
												const content = e.clipboardData.getData('text/plain');
												const values = content
													.trim()
													.split('\t')
													.map((v) => +v.replaceAll(',', '.'));
												if (values.length + +fieldName > yearRange[1]) {
													modal.error({
														message: `
														You can only paste ${
															yearRange[1] - +fieldName
														} values from year ${fieldName}. <br/>
														${values.length} values were found in your clipboard.
														`,
													});
												} else {
													for (const [index, value] of values.entries()) {
														updateDemand({
															year: +fieldName + index,
															pc_growth: value,
															read_scenario:
																isSalesRegionSelected &&
																index === values.length - 1
																	? true
																	: false,
														});
													}
												}
											}}
											isPct={
												exists(row[fieldName]) &&
												((isSalesRegionSelected && rowIndex !== 2) ||
													(!isSalesRegionSelected && rowIndex !== 1))
											}
											onFocus={onFocus}
										/>
									</td>
								</Tippy>
							);
						}}
					/>
				)}
			</ScenarioContentContainer>
		</div>
	);
};

export default Demand;
